/// <reference types="google.maps" />
// Third-party Deps
import {geohashQueryBounds} from 'geofire-common';
import {compressObjectToBase64, decompressBase64ToObject} from '@northpoint-development/labs-common-core';

// Classes and helpers
import {queryCollection, queryCollectionByIds, addDocument, deleteDocument, getSubcollection, getSubcollectionDocument, duplicateDocument, currentUser, updateDocument, addSubcollectionDocument, getDocument} from '../FirestoreService';
import {gmAPI, MapService} from '../MapService';
import {getCollection} from '../../lib/filters/filters';
import {Overlay} from './Overlay';
import {ConfigService} from '@/services/ConfigService';
import {wrapError} from '@/lib/errors';
import {FEATURES_COLLECTION, OVERLAY_COLLECTION} from '@/lib/constants';

/**
 * @typedef {import('firebase/firestore').DocumentData} DocumentData
 * @typedef {import('@/services/OverlaysService/Feature').Feature} Feature
 */

const OverlayService = {
  /**
   * featuresByGeohash accepts a center and a radius, retrieves all features from Firestore within the radius, and adds
   * them to the right collections. Any parent overlay of any feature added will also be initialized and added.
   * @param {google.maps.LatLng|google.maps.LatLngLiteral} center center of the circle
   * @param {number} radiusInMeters radius of the circle in meters
   * @param {boolean} [targetTaxFeatures] Whether to fetch Tax or Standard Features.
   * @return {Promise<void | false>}
   */
  featuresByGeohash: async function(center, radiusInMeters, targetTaxFeatures = false) {
    const overlaysCollection = getCollection('overlays');
    if (!overlaysCollection) {
      console.error('featuresByGeohash: No overlay collection found');
      return false;
    }

    const featureCollection = targetTaxFeatures ? getCollection('taxFeatures') : getCollection('features');
    const {lat, lng} = center instanceof gmAPI.LatLng ? {lat: center.lat(), lng: center.lng()} : center;

    // Geohashes allow us to approximate area queries by joining a set of queries on the Geohash field.
    // Each arr in the query results is a start/end pair, and we need to query Firestore for each pair.
    // There can be up to 9 pairs of bounds depending on overlap, but in most cases there are 4.
    const geohashes = geohashQueryBounds([lat, lng], radiusInMeters);
    const promises = geohashes.map(([start, end]) => fetchFeatureDocsByRange(start, end, targetTaxFeatures));
    // create a list of all the features in view and all the parent overlays that do not yet exist.
    /** @type {Feature[]} */
    const features = [];
    /** @type {string[]} */
    const parentOverlays = [];
    (await Promise.allSettled(promises))
      .flat()
      .forEach((result) => {
        if (result.status !== 'fulfilled') {
          console.error(`featuresByGeohash: query failed: ${result.reason}`);
          return;
        }
        if (!(result.value.length > 0)) return;
        result.value.forEach((f) => {
          // Only features that are not already in the collection need to be initialized.
          if (featureCollection.sourceHasId(f.id)) return;
          features.push(f);
          // only parent overlays that are not already in the collection need to be initialized.
          if (overlaysCollection.sourceHasId(f.parentId) || parentOverlays.includes(f.parentId)) return;
          parentOverlays.push(f.parentId);
        });
      });

    // add the features to their parent overlay and create the overlays.
    // Delete any features created this way from the list.
    if (parentOverlays.length > 0) {
      const toAdd = [];
      const docs = await queryCollectionByIds(OVERLAY_COLLECTION, parentOverlays, true);
      for (const doc of docs) {
        try {
          doc.features = new Map();
          for (let i = features.length - 1; i >= 0; i--) { // loop backwards to avoid index changes when splicing
            if (features[i].parentId === doc.id) {
              doc.features.set(features[i].id, ...features.splice(i, 1));
            }
          }
          doc.folders = await decompressBase64ToObject(doc.folders);
          toAdd.push(new Overlay(doc));
        } catch (error) {
          // A corrupt overlay doc (BMAPS-3652) must not abort the whole load; skip it and its features.
          console.error(wrapError(error, `featuresByGeohash: skipping overlay ${doc.id}`));
        }
      }
      if (toAdd.length > 0) {
        overlaysCollection.addItems(...toAdd);
      }
    }

    /** @type {Set<Overlay>} */
    const updatedParents = new Set();
    /** @type {Feature[]} */
    const newFeatures = [];
    // Any features remaining belong to a parent overlay that already exists.
    features.forEach((f) => {
      /** @type {Overlay} */
      const parentOverlay = overlaysCollection.getItem(f.parentId);
      if (parentOverlay) {
        newFeatures.push(parentOverlay.addFeature(f));
        // Add to set
        updatedParents.add(parentOverlay);
      }
    });
    if (newFeatures.length > 0) {
      featureCollection.addItems(...newFeatures);
    }
    // For every parent updated by getting features at a geohash, update the feature render order
    updatedParents.forEach((overlay) => {
      overlay.updateFeatureRenderOrder();
    });
  },
  /**
   * overlayById gets an overlay based on the Id Provided
   * @param {string} overlayId the Document ID of the overlay to retrieve
   * @return {Promise<Overlay[] | false>}
   */
  overlayById: async function(overlayId) {
    const overlaysCollection = getCollection('overlays');
    if (!overlaysCollection) {
      console.error('overlaysById: No overlay collection found');
      return false;
    }

    const result = await queryCollectionByIds(OVERLAY_COLLECTION, [overlayId], true);
    if (!result || result.length === 0) {
      console.error(`overlayById: No overlay found for ID: ${overlayId}`);
      return false;
    }

    return this._initializeOverlaysFromData(result);
  },

  /**
   * Duplicates an Overlay, adding it and any Features to the proper collections.
   * @param {Overlay} overlay
   * @return {Promise<Overlay>} The duplicated Overlay.
   */
  duplicateOverlay: async function(overlay) {
    /** @type {string} */
    let newOverlayId;

    try {
      const userIsOnTaxTeam = ConfigService.userIsOnTeam('tax');
      newOverlayId = await duplicateDocument(OVERLAY_COLLECTION, overlay.id);

      const featureIdsOldToNew = {};
      const oldFeatures = await getSubcollection(OVERLAY_COLLECTION, overlay.id, FEATURES_COLLECTION, true);

      // Linked labels are copied last so their source feature's new ID is known and the link can point at the copy
      const linkedLabels = oldFeatures.filter((f) => f.graphicData?.linkedLabel);
      const otherFeatures = oldFeatures.filter((f) => !f.graphicData?.linkedLabel);
      const orderedFeatures = otherFeatures.concat(linkedLabels);

      for (const f of orderedFeatures) {
        const data = {...f, isTaxFeature: userIsOnTaxTeam};
        const linkedLabel = f.graphicData?.linkedLabel;
        const newSourceId = linkedLabel && featureIdsOldToNew[linkedLabel.featureId];
        if (newSourceId) {
          const newLinkedLabel = {...linkedLabel, featureId: newSourceId};
          data.graphicData = {...f.graphicData, linkedLabel: newLinkedLabel};
        }
        const newFeatureId = await addSubcollectionDocument(
          OVERLAY_COLLECTION, newOverlayId, FEATURES_COLLECTION, data,
        );
        featureIdsOldToNew[f.id] = newFeatureId;
        f.id = newFeatureId; // Update the ID of the feature to the new one
      }

      // We must assign the new Feature IDs to the copied Folders
      const foldersCopy = JSON.parse(JSON.stringify(overlay.folders));
      const updateFolderFeatureIds = (folders) => {
        return folders.map((folder) => {
          if (folder.children) {
            folder.children = updateFolderFeatureIds(folder.children);
          } else {
            folder.id = featureIdsOldToNew[folder.id];
          }
          return folder;
        });
      };
      const user = currentUser();
      const created = new Date();
      const updatedData = {
        name: `COPY - ${overlay.name}`,
        createdBy: user.displayName,
        createdByUid: user.uid,
        createdAt: created,
        editedAt: created,
        editedBy: user.displayName,
        editedByUid: user.uid,
        isTaxOverlay: userIsOnTaxTeam,
        folders: updateFolderFeatureIds(foldersCopy),
      };

      await updateDocument(OVERLAY_COLLECTION, newOverlayId, {
        ...updatedData,
        folders: await compressObjectToBase64(updatedData.folders),
      });

      const newOverlay = await getDocument(OVERLAY_COLLECTION, newOverlayId);

      if (!newOverlay) {
        throw new Error(`Document with ID ${newOverlayId} not found in collection ${OVERLAY_COLLECTION}`);
      }

      newOverlay.id = newOverlayId;
      newOverlay.features = new Map();

      const newFeatures = await getSubcollection(OVERLAY_COLLECTION, newOverlayId, FEATURES_COLLECTION, true);

      for (const f of newFeatures) {
        newOverlay.features.set(f.id, f);
      }

      const o = new Overlay({...newOverlay, folders: await decompressBase64ToObject(newOverlay.folders)});
      getCollection('overlays').addItems(o);

      return o;
    } catch (error) {
      if (newOverlayId) {
        // We need to clean up the Overlay if it was created before the error occurred
        await deleteDocument(OVERLAY_COLLECTION, newOverlayId, [FEATURES_COLLECTION]);
        const overlayInCol = getCollection('overlays').getItem(newOverlayId);
        if (overlayInCol) {
          getCollection('overlays').removeItems(overlayInCol);
        }
      }
      error.message = `Could not duplicate Overlay: ${error.message}`;
      throw error;
    }
  },

  /**
   * copies a feature from one overlay to another.
   * @param {Feature} feature the feature to copy
   * @param {Overlay} originOverlay the overlay to copy the feature from
   * @param {Overlay} targetOverlay the overlay to copy the feature to
   */
  copyFeatureToOverlay: async function (feature, originOverlay, targetOverlay) {
    const subDoc = await getSubcollectionDocument('beyondMaps-overlay', originOverlay.id, 'features', feature.id);
    const docId = await addSubcollectionDocument('beyondMaps-overlay', targetOverlay.id, 'features', subDoc);
    subDoc.id = docId;
    // Add feature the local collection
    const featureCollection = targetOverlay.isTaxOverlay ? getCollection('taxFeatures') : getCollection('features');
    featureCollection.addItems(targetOverlay.addFeature(subDoc));
  },

  /**
   * overlaysByParent retrieves all overlays from firestore where the overlay's parent.id matches the provided parentId.
   * Overlays are added to the collections and also returned. Returns an empty array if no overlays are found.
   * @param {string} parentId parentId unique identifier for the parent project.
   * @return {Promise<Overlay[] | []>}
   */
  overlaysByParent: async function(parentId) {
    const overlaysCollection = getCollection('overlays');
    if (!overlaysCollection) {
      console.error('overlaysByParent: No overlay collection found');
      return [];
    }
    const overlayDocs = (await queryCollection(
      OVERLAY_COLLECTION,
      {where: [
        {opStr: '==', value: parentId, field: 'parent.id'},
      ]},
      true,
    )).filter((doc) => !doc.private); // Do not return private overlays
    if (overlayDocs.length === 0) {
      return [];
    }

    return this._initializeOverlaysFromData(overlayDocs);
  },

  /**
   * Fetches all Overlays from Firestore and initializes the Overlays collection.
   * @return {Promise<Overlay[] | false>}
   */
  fetchAll: async function() {
    const queryParams = {
      orderBy: [
        {field: 'editedAt', order: 'desc'}, // Order by most recent first
      ],
    };
    // Query the collection & load the retrieved overlays.
    return this._initializeOverlaysFromData(
      await queryCollection(OVERLAY_COLLECTION, queryParams, true),
    );
  },

  /**
   * Retrieves all features that belong to the overlay identified by `parentId`.
   * @param {string} parentId Unique identifier for the parent project.
   */
  featuresByParent: async function(parentId) {
    /** @type {Overlay} */
    const overlay = getCollection('overlays').getItem(parentId);
    if (!overlay) {
      console.warn(`Attempted to find a parent overlay with the given ID of ${parentId}, but none were found.`);
      return false;
    }

    // Get features from firestore subcollection
    const features = await getSubcollection(OVERLAY_COLLECTION, parentId, FEATURES_COLLECTION, true);

    // Add features to the overlay and then add them to the features collection
    /** @type {Feature[]} */
    const newFeatures = [];
    features.forEach((feature) => {
      if (overlay.features.has(feature.id)) return;
      newFeatures.push(overlay.addFeature(feature));
    });
    if (newFeatures.length > 0) {
      const featureCollection = overlay.isTaxOverlay ? getCollection('taxFeatures') : getCollection('features');
      // Re-anchor the viewport filter to the current map position. It still holds the center from the
      // last geohash load, which may be elsewhere — silently excluding these features from rendering.
      const mapCenter = MapService.getMap()?.getCenter();
      if (mapCenter) {
        const radius = MapService.getMapRadius();
        featureCollection.addFilter('mapBounds', (feature) => feature.isInRange(mapCenter, radius));
      }
      featureCollection.addItems(...newFeatures);
    }
  },

  /**
   * Retrieves a user's private overlays from Firestore.
   * @param {string} userId User's Firebase UID
   * @return {Promise<Overlay[] | undefined>} `undefined` if the user has no private overlays.
   */
  fetchPrivateOverlays: async function(userId) {
    /** @type {DocumentData[]} */
    const result = await queryCollection(OVERLAY_COLLECTION, {
      where: [
        {field: 'createdByUid', opStr: '==', value: userId},
        {field: 'private', opStr: '==', value: true},
      ],
    }, true);
    return result.length ? this._initializeOverlaysFromData(result) : undefined;
  },

  /**
   * _initializeOverlaysFromData takes firestore overlay documents and returns overlay objects.
   * @param {DocumentData[]} overlayData The firestore overlay docs to turn to Overlays
   * @return {Overlay[]} The overlays from overlayData
   */
  _initializeOverlaysFromData: async function(overlayData) {
    const overlaysCollection = getCollection('overlays');
    if (!overlaysCollection) {
      console.error('_initializeOverlaysFromData: No overlay collection found');
      return [];
    }
    /** @type {Overlay[]} */
    const toAdd = [];
    const overlays = [];

    for (const doc of overlayData) {
      try {
        /** @type {Overlay} */
        const existingOverlay = overlaysCollection.getItem(doc.id);
        if (!existingOverlay) {
          doc.folders = await decompressBase64ToObject(doc.folders);
          const overlay = new Overlay(doc);
          toAdd.push(overlay);
          overlays.push(overlay);
        } else {
          overlays.push(existingOverlay);
        }
      } catch (error) {
        console.error(wrapError(error, `Failed to initialize overlay with ID ${doc.id}`));
      }
    }

    // If new Overlays were created, register them in the 'overlays' collection.
    if (toAdd.length) {
      overlaysCollection.addItems(...toAdd);
    }
    return overlays;
  },

  /**
   * newOverlay creates a new overlay in Firestore, adds it to the collections, and returns it.
   * @param {Object} overlayData data to create the overlay with
   * @return {Promise<Overlay | false>}
   */
  newOverlay: async function(overlayData) {
    const overlaysCollection = getCollection('overlays');
    if (!overlaysCollection) {
      console.error('newOverlay: No overlay collection found');
      return false;
    }

    const id = await addDocument(OVERLAY_COLLECTION, {
      ...overlayData,
      folders: await compressObjectToBase64(overlayData.folders || []),
    });

    overlayData.id = id;

    const overlay = new Overlay(overlayData);
    overlaysCollection.addItems(overlay);

    return overlay;
  },
  /**
   * Delete an overlay from Firestore and remove it from the collection.
   * @param {Overlay} overlay The overlay to delete.
   */
  deleteOverlay: async function(overlay) {
    const overlaysCollection = getCollection('overlays');
    if (!overlaysCollection) {
      console.error('deleteOverlay: No overlay collection found');
      return;
    }
    overlaysCollection.removeItems(overlay);
    // tax overlays are not removed from the map by the subscription, manually do so here.
    if (overlay.isTaxOverlay) {
      overlay.setRender(false);
    }
    await deleteDocument(OVERLAY_COLLECTION, overlay.id, [FEATURES_COLLECTION]);
  },

  /**
   * Pan and zoom the map to the given feature
   * @param {Feature} feature - the feature to be centered on and zoomed to
   */
  async panAndZoomToFeature(feature) {
    const defaultZoom = 15;
    // If the zoom level is lower than default zoom, zoom in to default. Otherwise keep at current zoom level
    const zoom = (MapService.getMap().getZoom() < defaultZoom) ? defaultZoom : MapService.getMap().getZoom();

    const featureOutsideBounds = !MapService.getMap().getBounds().contains(feature.center);
    const centerViewport = MapService.getMap().getZoom() < defaultZoom ||featureOutsideBounds;

    // If the overlay is already in view and the zoom is less than the default, no need to center the viewport
    if (feature && centerViewport) {
      await MapService.centerViewport(feature.center, zoom);
    }
  },

};

/**
 * @param {string} start start of geohash range
 * @param {string} end end of geohash range
 * @param {boolean} [targetTaxFeatures] Whether to fetch Tax or Standard Features.
 * @return {Promise<DocumentData[]>}
 */
const fetchFeatureDocsByRange = (start, end, targetTaxFeatures = false) => {
  return queryCollection(
    FEATURES_COLLECTION,
    {
      orderBy: [{field: 'geohash', order: 'asc'}],
      range: {start, end},
      where: [
        {field: 'isTaxFeature', opStr: '==', value: targetTaxFeatures},
      ],
      isSubcollection: true,
      resolveParentId: true,
    },
    true,
  );
};

export {
  OverlayService,
};
